import { useState, type ComponentType } from 'react'
import type { CalendarProps } from '@/lib/render'
import type { CalendarViewProps } from './calendar/shared'
import { StatusLegend } from './calendar/shared'
import { DayCalendarView } from './calendar/DayCalendarView'
import { WeekCalendarView } from './calendar/WeekCalendarView'
import { ResourceCalendarView } from './calendar/ResourceCalendarView'
import { SlotActions } from '@/components/calendar/SlotActions'
import { RescheduleDialog } from '@/components/calendar/RescheduleDialog'
import { localDateTime, type MoveProposal, type SlotPick } from '@/lib/slotBooking'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type CalendarViewName = NonNullable<CalendarProps['view']>

const CALENDAR_VIEWS: Record<CalendarViewName, ComponentType<CalendarViewProps>> = {
  day: DayCalendarView,
  week: WeekCalendarView,
  resources: ResourceCalendarView,
}

interface CalendarRendererProps {
  props: unknown
  /**
   * Make appointment tiles clickable quick-action menus AND empty time clickable
   * to book. Defaults OFF — this renderer also draws CUSTOMER-facing chat
   * calendars, and neither "mark paid" nor a booking form over the whole business
   * may ever appear on one. Only an operator surface passes it.
   */
  interactive?: boolean
  onChanged?: () => void
  /**
   * Draw every machine's column and a grid even on an empty day. The /schedule
   * screen passes it; chat calendars stay compact. See CalendarViewProps.
   */
  showAllResources?: boolean
  /** Leave out the date line above the grid. See CalendarViewProps. */
  hideDateHeading?: boolean
  /**
   * Leave out the count, hints and key under the grid. /schedule states the
   * count in its header and keeps the key behind a button; chat calendars need both.
   */
  hideFooter?: boolean
  /** Take the parent's whole width and height. See CalendarViewProps. */
  fill?: boolean
}

export function CalendarRenderer({ props, interactive, onChanged, showAllResources, hideDateHeading, hideFooter, fill }: CalendarRendererProps) {
  const data = props as CalendarProps
  // The clicked slot lives here rather than in the views: all three views can
  // raise a pick, and the dialog must sit OUTSIDE the scrolling grid so it is
  // not clipped by the column's overflow.
  const [pick, setPick] = useState<SlotPick | null>(null)
  // A dropped appointment, for the same reason. A drag PROPOSES and the dialog
  // confirms — the drop itself never writes, because a day grid is a dense
  // target and a mis-drag would move a real customer's appointment silently.
  const [move, setMove] = useState<MoveProposal | null>(null)

  if (!data?.date || !Array.isArray(data.events)) {
    return (
      <div className="rounded-lg bg-destructive/10 p-4">
        <p className="text-[13px] text-destructive">Invalid calendar data</p>
      </div>
    )
  }

  const viewName = data.view ?? 'day'
  const ViewComponent = CALENDAR_VIEWS[viewName] ?? DayCalendarView
  const count = data.events.length

  return (
    <>
      <Card className={cn('gap-0 overflow-hidden rounded-xl py-0', fill && 'flex min-h-0 flex-col')}>
        <ViewComponent
          data={data}
          fill={fill}
          interactive={interactive}
          onChanged={onChanged}
          showAllResources={showAllResources}
          hideDateHeading={hideDateHeading}
          // Booking is gated by the same operator check as the quick actions.
          onPickSlot={interactive ? setPick : undefined}
          // Dragging an appointment to a new time, same gate. The week view
          // ignores this — it is a list of cards with no time axis, so a drag
          // there would know a date and no time.
          onMoveBooking={interactive ? setMove : undefined}
        />
        {hideFooter ? null : (
          <div className="flex flex-col gap-1.5 border-t border-border bg-muted px-3 py-2">
            <p className="text-xs text-muted-foreground">
              {count} appointment{count !== 1 ? 's' : ''}
              {interactive ? ' · tap an empty slot to book, block or note · drag an appointment to move it' : ''}
            </p>
            <StatusLegend />
          </div>
        )}
      </Card>

      <SlotActions
        pick={pick}
        onOpenChange={(open) => { if (!open) setPick(null) }}
        onChanged={onChanged}
      />

      {/*
        ONE dialog serves both routes to a move — the drag drop here, and "Move to
        another time" in the booking sheet. A second component would be a second
        definition of what moving an appointment means, and the two would drift.
        A drag arrives with `proposedStart` filled in and the dialog opens on a
        confirmation; the sheet passes none and it opens on the slot browser.
      */}
      <RescheduleDialog
        event={move?.event ?? null}
        // A CalendarEvent carries the service NAME but not its id, so the dialog
        // resolves it from the booking itself. The sheet passes the id it already
        // fetched; this path has none to give.
        serviceId={null}
        proposedStart={move ? localDateTime(move.date, move.startMinutes) : null}
        onOpenChange={(open) => { if (!open) setMove(null) }}
        onChanged={onChanged}
      />
    </>
  )
}
